import { getModule, Module, Mutation, Action, VuexModule } from 'vuex-module-decorators';
import { store } from '../..';
import { TranslateResult } from 'vue-i18n';
import { GenerateRecommendationLetterRequest } from '@/models/student/generate-recommendation-letter-request';
import { RecommendationLetterUpdateRequest } from '@/models/student/recommendation-letter-update-request';
import { GeneratedRecommendationLetterItem } from '@/models/student/recommendation-letter';
import { studentModule } from './student.module';

export enum ConfirmationStep {
  Show = 0,
  Confirm = 1,
  Edit = 2,
  Download = 3,
}

export enum RecommendationLetterStep {
  List = 0,
  StudentInfo = 1,
  PolicySelection = 2,
  Generation = 3,
}

export enum StudentInfoSectionType {
  TargetInstitution = 1,
  RecommendationGuidelines = 2,
  AdmissionPolicy = 3,
  Achievements = 4,
  MostSignificantAchievement = 5,
  MotivationLetter = 6,
  PersonalityEpisode = 7,
  DesiredLength = 8,
  GenerationPolicy = 9,
}

export interface RecommendationLetterForm {
  targetInstitution: string;
  recommendationGuidelines: string;
  admissionPolicy: string;
  achievements: string;
  mostSignificantAchievement: string;
  motivationLetter: string;
  personalityEpisode: string;
  desiredLength: number;
  temperature: number;
}

export interface IRecommendationLetterState {
  currentStep: RecommendationLetterStep;
  currentStudentInfoSection: StudentInfoSectionType;
  form: RecommendationLetterForm;
  generationPreferences: string[];
  selectedGenerationPreferences: string[];
  isBusy: boolean;
  generatedRecommendationLetters: GeneratedRecommendationLetterItem[];
  selectedRecommendationLetterId: number | null;
  confirmationStep: ConfirmationStep;
}

@Module({ dynamic: true, store, name: 'recommendationLetterModule', namespaced: true })
class RecommendationLetterModule extends VuexModule implements IRecommendationLetterState {
  currentStep: RecommendationLetterStep = RecommendationLetterStep.List;
  currentStudentInfoSection: StudentInfoSectionType = StudentInfoSectionType.TargetInstitution;

  form: RecommendationLetterForm = {
    targetInstitution: '',
    recommendationGuidelines: '',
    admissionPolicy: '',
    achievements: '',
    mostSignificantAchievement: '',
    motivationLetter: '',
    personalityEpisode: '',
    desiredLength: 1000,
    temperature: 1.0,
  };

  generationPreferences: string[] = [];
  selectedGenerationPreferences: string[] = [];
  isBusy: boolean = false;
  generatedRecommendationLetters: GeneratedRecommendationLetterItem[] = [];
  selectedRecommendationLetterId: number | null = null;
  confirmationStep: ConfirmationStep = ConfirmationStep.Show;
  loadingText = 'Loading...';

  // Getters - 個別プロパティアクセス
  get targetInstitution(): string {
    return this.form.targetInstitution;
  }

  get recommendationGuidelines(): string {
    return this.form.recommendationGuidelines;
  }

  get admissionPolicy(): string {
    return this.form.admissionPolicy;
  }

  get achievements(): string {
    return this.form.achievements;
  }

  get mostSignificantAchievement(): string {
    return this.form.mostSignificantAchievement;
  }

  get motivationLetter(): string {
    return this.form.motivationLetter;
  }

  get personalityEpisode(): string {
    return this.form.personalityEpisode;
  }

  get desiredLength(): number {
    return this.form.desiredLength;
  }

  get temperature(): number {
    return this.form.temperature;
  }

  // 入力データがあるかをチェックするgetter
  get hasInputData(): boolean {
    return !!(
      this.form.targetInstitution ||
      this.form.recommendationGuidelines ||
      this.form.admissionPolicy ||
      this.form.achievements ||
      this.form.mostSignificantAchievement ||
      this.form.motivationLetter ||
      this.form.personalityEpisode
    );
  }

  // 選択中の推薦書を取得するgetter
  get selectedRecommendationLetter(): GeneratedRecommendationLetterItem | null {
    if (this.selectedRecommendationLetterId) {
      return this.generatedRecommendationLetters.find(letter => letter.id === this.selectedRecommendationLetterId) || null;
    }
    return this.generatedRecommendationLetters.length > 0 ? this.generatedRecommendationLetters[0] : null;
  }

  // Getters
  get currentStudentInfoCompleted(): boolean {
    if (this.currentStudentInfoSection === StudentInfoSectionType.MotivationLetter) {
      return true;
    }
    // セクション8（希望文字数 + 推薦度合い）は両方の条件をチェック
    if (this.currentStudentInfoSection === StudentInfoSectionType.DesiredLength) {
      const isLengthValid = this.desiredLength >= 1000 && this.desiredLength <= 4000;
      const isTemperatureValid = this.temperature > 0;
      return isLengthValid && isTemperatureValid;
    }

    // セクションタイプに応じて適切なフィールドをチェック
    switch (this.currentStudentInfoSection) {
      case StudentInfoSectionType.TargetInstitution:
        return !!this.form.targetInstitution;
      case StudentInfoSectionType.RecommendationGuidelines:
        return !!this.form.recommendationGuidelines;
      case StudentInfoSectionType.AdmissionPolicy:
        return !!this.form.admissionPolicy;
      case StudentInfoSectionType.Achievements:
        return !!this.form.achievements;
      case StudentInfoSectionType.MostSignificantAchievement:
        return !!this.form.mostSignificantAchievement;
      case StudentInfoSectionType.PersonalityEpisode:
        return !!this.form.personalityEpisode;
      default:
        return false;
    }
  }

  get allStudentInfoSectionsCompleted(): boolean {
    const requiredFields = [
      this.form.targetInstitution,
      this.form.recommendationGuidelines,
      this.form.admissionPolicy,
      this.form.achievements,
      this.form.mostSignificantAchievement,
      this.form.personalityEpisode,
      // motivationLetterは任意なので除外
    ];

    const allRequiredFieldsFilled = requiredFields.every(field => field.trim() !== '');
    const numbersValid = this.form.desiredLength >= 1000 && this.form.desiredLength <= 4000 && this.form.temperature > 0;
    const isAtFinalStep = this.currentStudentInfoSection === StudentInfoSectionType.GenerationPolicy;

    return allRequiredFieldsFilled && numbersValid && isAtFinalStep;
  }

  get generationParams() {
    return {
      universityInfo: this.form.targetInstitution,
      recommendationGuidelines: this.form.recommendationGuidelines,
      admissionPolicy: this.form.admissionPolicy,
      achievements: this.form.achievements,
      mostSignificantAchievement: this.form.mostSignificantAchievement,
      motivationLetter: this.form.motivationLetter,
      personalityEpisode: this.form.personalityEpisode,
      desiredLength: this.form.desiredLength,
      generationPreferences: this.selectedGenerationPreferences,
      temperature: this.form.temperature,
    };
  }

  @Mutation
  setTargetInstitution(value: string) {
    this.form.targetInstitution = value;
  }

  @Mutation
  setRecommendationGuidelines(value: string) {
    this.form.recommendationGuidelines = value;
  }

  @Mutation
  setAdmissionPolicy(value: string) {
    this.form.admissionPolicy = value;
  }

  @Mutation
  setAchievements(value: string) {
    this.form.achievements = value;
  }

  @Mutation
  setMostSignificantAchievement(value: string) {
    this.form.mostSignificantAchievement = value;
  }

  @Mutation
  setMotivationLetter(value: string) {
    this.form.motivationLetter = value;
  }

  @Mutation
  setPersonalityEpisode(value: string) {
    this.form.personalityEpisode = value;
  }

  @Mutation
  setDesiredLength(value: number) {
    this.form.desiredLength = value;
  }

  @Mutation
  setTemperature(value: number) {
    this.form.temperature = value;
  }

  @Mutation
  clearState() {
    this.currentStep = RecommendationLetterStep.List;
    this.currentStudentInfoSection = StudentInfoSectionType.TargetInstitution;
    this.form = {
      targetInstitution: '',
      recommendationGuidelines: '',
      admissionPolicy: '',
      achievements: '',
      mostSignificantAchievement: '',
      motivationLetter: '',
      personalityEpisode: '',
      desiredLength: 1000,
      temperature: 1.0,
    };
    this.generationPreferences = [];
    this.selectedGenerationPreferences = [];
    this.isBusy = false;
    this.generatedRecommendationLetters = [];
    this.selectedRecommendationLetterId = null;
    this.confirmationStep = ConfirmationStep.Show;
  }

  // Actions
  @Action
  async generateRecommendationLetter() {
    this.setIsBusy(true);
    const student = studentModule.student;
    const requestBody: GenerateRecommendationLetterRequest = {
      studentID: student.studentID,
      studentName: student.fullName,
      ...this.generationParams,
    };

    try {
      const result = await studentModule.generateRecommendationLetter(requestBody);

      // 新しい配列形式のレスポンスかチェック
      if ((result as any).recommendationLetters) {
        // 新しい形式
        this.setGeneratedRecommendationLetters((result as any).recommendationLetters);
        if ((result as any).recommendationLetters.length > 0) {
          this.setSelectedRecommendationLetterId((result as any).recommendationLetters[0].id);
        }
      } else {
        // 古い形式への後方互換性
        const oldResult = result as any;
        const letter = new GeneratedRecommendationLetterItem().deserialize({
          id: oldResult.recommendationLetterId || Date.now(),
          title: '推薦書',
          content: oldResult.recommendationLetter || '',
        });
        this.setGeneratedRecommendationLetters([letter]);
        this.setSelectedRecommendationLetterId(letter.id);
      }

      this.setCurrentStep(this.currentStep + 1);
    } catch (error) {
      console.error('Error generating recommendation letter:', error);
      throw error;
    } finally {
      this.setIsBusy(false);
    }
  }

  @Action
  async fetchRecommendationLetterPolicies() {
    const student = studentModule.student;
    const requestBody: GenerateRecommendationLetterRequest = {
      studentID: student.studentID,
      studentName: student.fullName,
      ...this.generationParams,
    };

    try {
      const result = await studentModule.generateRecommendationLetterPolicies(requestBody);
      this.setGenerationPreferences(result.policies);
      this.setSelectedGenerationPreferences([]);
    } catch (error) {
      console.error('Error fetching recommendation letter policies:', error);
      throw error;
    }
  }

  @Action
  async deleteRecommendationLetter(id: number) {
    const student = studentModule.student;
    await studentModule.deleteRecommendationLetter({ studentID: student.studentID, id });
    await studentModule.fetchStudentRecommendationLetters(student.studentID);
  }

  @Action
  async updateRecommendationLetter(payload: { id: number; title: string; content: string }) {
    this.setIsBusy(true);
    const student = studentModule.student;
    const requestBody: RecommendationLetterUpdateRequest = {
      id: payload.id,
      title: payload.title,
      content: payload.content,
      studentID: student.studentID,
    };

    try {
      await studentModule.updateRecommendationLetter(requestBody);
      this.updateGeneratedRecommendationLetter(payload);
      await studentModule.fetchStudentRecommendationLetters(student.studentID);
    } catch (error) {
      throw error;
    } finally {
      this.setIsBusy(false);
    }
  }

  @Mutation
  setLoadingText(text?: string) {
    this.loadingText = text || 'Loading...';
  }

  @Mutation
  setCurrentStep(step: RecommendationLetterStep) {
    this.currentStep = step;
  }

  @Mutation
  setCurrentStudentInfoSection(section: StudentInfoSectionType) {
    this.currentStudentInfoSection = section;
  }

  @Mutation
  setGenerationPreferences(preferences: string[]) {
    this.generationPreferences = preferences;
  }

  @Mutation
  setSelectedGenerationPreferences(preferences: string[]) {
    this.selectedGenerationPreferences = preferences;
  }

  @Mutation
  toggleGenerationPreference(preference: string) {
    const index = this.selectedGenerationPreferences.indexOf(preference);
    if (index > -1) {
      // すでに選択されている場合は削除
      this.selectedGenerationPreferences.splice(index, 1);
    } else {
      // 選択されていない場合で、3つ未満の場合は追加
      if (this.selectedGenerationPreferences.length < 3) {
        this.selectedGenerationPreferences.push(preference);
      }
    }
  }

  @Mutation
  setGeneratedRecommendationLetters(letters: GeneratedRecommendationLetterItem[]) {
    this.generatedRecommendationLetters = letters;
  }

  @Mutation
  setSelectedRecommendationLetterId(id: number | null) {
    this.selectedRecommendationLetterId = id;
  }

  @Mutation
  updateGeneratedRecommendationLetter(payload: { id: number; title: string; content: string }) {
    const letter = this.generatedRecommendationLetters.find(l => l.id === payload.id);
    if (letter) {
      letter.title = payload.title;
      letter.content = payload.content;
    }
  }

  @Mutation
  setConfirmationStep(step: ConfirmationStep) {
    this.confirmationStep = step;
  }

  @Mutation
  setIsBusy(isBusy: boolean) {
    this.isBusy = isBusy;
  }
}

export const recommendationLetterModule = getModule(RecommendationLetterModule);
